/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { Loader, Button, Icon, Input, Select } from "semantic-ui-react";
import { Notification } from "../../../api";
import { useAuth, useNotifications } from "../../../hooks";
import { ListNotifications } from "../../../components/Student/MyNotifications";
import "./MyNotifications.scss";

const notificationController = new Notification();

export function MyNotifications() {
  const { user, accessToken } = useAuth();
  const { reloadNotifications } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [reload, setReload] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const onReload = () => setReload((prevState) => !prevState);

  const handleItemsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (e, { value }) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e, { value }) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const getStatusColor = (viewed) => {
    return viewed ? "blue" : "grey";
  };

  const getStatusText = (viewed) => {
    return viewed ? "LEÍDA" : "NO LEÍDA";
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "grade":
        return "orange";
      case "payment":
        return "teal";
      case "forum":
        return "purple";
      case "course":
        return "blue";
      case "event":
        return "olive";
      case "general":
        return "grey";
      default:
        return "brown";
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case "grade":
        return "CALIFICACIÓN";
      case "payment":
        return "PAGO";
      case "forum":
        return "FORO";
      case "course":
        return "CURSO";
      case "event":
        return "EVENTO";
      case "general":
        return "GENERAL";
      default:
        return "SISTEMA";
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationController.updateNotification(notificationId, {
        viewed: true,
      });
      onReload();
      reloadNotifications();
    } catch (error) {
      // Error handling
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationController.readAllNotifications(user.id);
      onReload();
      reloadNotifications();
    } catch (error) {
      // Error handling
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationController.deleteNotification(notificationId);
      onReload();
      reloadNotifications();
    } catch (error) {
      // Error handling
    }
  };

  const tableConfig = {
    headers: ["Fecha", "Tipo", "Título", "Mensaje", "Estado", "Acciones"],
    fields: [
      {
        field: "createdAt",
        dataType: "date",
        format: "DD/MM/YYYY hh:mm a",
        align: "center",
      },
      {
        field: "type",
        component: "Label",
        getColor: getTypeColor,
        getText: getTypeText,
        align: "center",
      },
      {
        field: "title",
        dataType: "text",
        align: "left",
      },
      {
        field: "message",
        dataType: "text",
        align: "left",
      },
      {
        field: "viewed",
        component: "Label",
        getColor: getStatusColor,
        getText: getStatusText,
        align: "center",
      },
      {
        buttons: [
          {
            color: "green",
            icon: "check",
            placeholder: "Marcar como leída",
            onClick: (row) => markAsRead(row.id),
            disabled: "row.viewed === true",
          },
          {
            color: "red",
            icon: "trash",
            placeholder: "Eliminar notificación",
            onClick: (row) => deleteNotification(row.id),
          },
        ],
      },
    ],
    columnWidths: ["15%", "12%", "20%", "30%", "13%", "10%"],
  };

  useEffect(() => {
    (async () => {
      setLoadingNotifications(true);
      try {
        const filters = {
          type: typeFilter,
          viewed: statusFilter,
          search: search,
        };
        
        const response = await notificationController.getNotificationsPaginated(
          currentPage,
          limit,
          filters
        );
        setNotifications(response);
      } catch (error) {
        // Error handling
      } finally {
        setLoadingNotifications(false);
      }
    })();
  }, [currentPage, limit, reload, accessToken, typeFilter, statusFilter, search]);

  return (
    <div className="student-my-notifications">
      <h1 className="student-my-notifications__title">Mis Notificaciones</h1>
      <p className="student-my-notifications__description">
        Aquí puedes ver todas tus notificaciones del sistema, incluyendo
        información sobre calificaciones, pagos, cursos y eventos importantes de
        tu proceso educativo.
      </p>
      <div className="student-my-notifications__filters">
        <Select
          className="edunova-select"
          placeholder="Filtrar por tipo"
          options={[
            { key: "", value: "", text: "Todos los tipos" },
            { key: "grade", value: "grade", text: "Calificación" },
            { key: "payment", value: "payment", text: "Pago" },
            { key: "forum", value: "forum", text: "Foro" },
            { key: "course", value: "course", text: "Curso" },
            { key: "event", value: "event", text: "Evento" },
            { key: "general", value: "general", text: "General" },
          ]}
          value={typeFilter}
          onChange={handleTypeFilterChange}
          clearable
        />
        <Select
          className="edunova-select"
          placeholder="Filtrar por estado"
          options={[
            { key: "", value: "", text: "Todos los estados" },
            { key: "unread", value: false, text: "No leídas" },
            { key: "read", value: true, text: "Leídas" },
          ]}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          clearable
        />
        <Input
          icon="search"
          iconPosition="left"
          type="text"
          placeholder="Buscar por título o mensaje..."
          value={search}
          onChange={handleSearchChange}
        />
        <Button
          onClick={() => {
            setSearch("");
            setTypeFilter("");
            setStatusFilter("");
            setCurrentPage(1);
          }}
        >
          <Icon name="refresh" />
          Limpiar filtros
        </Button>
        <Button
          color="green"
          onClick={markAllAsRead}
          disabled={!notifications.data || notifications.data.length === 0}
        >
          <Icon name="check circle" />
          Marcar todas como leídas
        </Button>
      </div>
      <ListNotifications
        notifications={notifications?.data}
        loading={loadingNotifications || !notifications}
        error={null}
        pagination={notifications?.pagination}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        tableConfig={tableConfig}
        onReload={onReload}
      />
    </div>
  );
}
